// src/pages/VerifyEmail.js
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { setAuth } from '../utils/auth';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';

// Resend verification form component
const ResendVerificationForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { showSuccess, showError } = useNotification();

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email) {
      showError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/resend-verification', { email });
      setSent(true);
      showSuccess('Verification email sent! Please check your inbox.');
    } catch (err) {
      // Backend returns success even if email doesn't exist (security)
      setSent(true);
      showSuccess('If an account with that email exists, a verification email has been sent.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-400 dark:border-green-800 rounded-lg mb-4">
        <p className="text-sm text-green-700 dark:text-green-400">
          ✓ Verification email sent! Please check your inbox.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleResend} className="mb-4">
      <p className="text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
        Resend verification email:
      </p>
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
          className="flex-1 px-3 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          {loading ? 'Sending...' : 'Resend'}
        </button>
      </div>
    </form>
  );
};

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const hasVerifiedRef = useRef(false); // Prevent duplicate requests (ref persists across re-renders)
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  useEffect(() => {
    // Prevent duplicate requests (React StrictMode causes double execution in dev)
    if (hasVerifiedRef.current || status !== 'verifying') {
      return;
    }
    
    hasVerifiedRef.current = true; // Mark as started immediately

    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Verification token is missing. Please check your email and use the complete verification link.');
        showError('Verification token is missing');
        return;
      }

      try {
        const response = await api.get(`/auth/verify-email/${token}`);
        
        // Auto-login user after successful verification
        if (response.data.token && response.data.user) {
          setAuth(response.data.token, response.data.user);
          setStatus('success');
          setMessage('Email verified successfully! You are now logged in.');
          showSuccess('Email verified successfully!');
          
          // Redirect based on user role
          setTimeout(() => {
            const user = response.data.user;
            if (user.role === 'admin') {
              navigate('/admin');
            } else if (user.role === 'journalist') {
              navigate('/journalist');
            } else {
              navigate('/dashboard');
            }
          }, 2000);
        }
      } catch (err) {
        // If token already used (400 error), check if user is already logged in
        // This handles React StrictMode double-request case
        if (err.response?.status === 400) {
          const existingToken = localStorage.getItem('token');
          const existingUser = localStorage.getItem('user');
          
          // If user is already logged in, verification likely succeeded in first request
          if (existingToken && existingUser) {
            try {
              const user = JSON.parse(existingUser);
              if (user.email_verified) {
                // User is verified and logged in - verification succeeded!
                setStatus('success');
                setMessage('Email already verified! You are logged in.');
                showSuccess('Email already verified!');
                
                setTimeout(() => {
                  if (user.role === 'admin') {
                    navigate('/admin');
                  } else if (user.role === 'journalist') {
                    navigate('/journalist');
                  } else {
                    navigate('/dashboard');
                  }
                }, 2000);
                return;
              }
            } catch (parseError) {
              // Continue to error handling
            }
          }
        }
        
        // Real error - token invalid or expired
        setStatus('error');
        let errorMsg = 'Email verification failed';
        if (err.response?.data?.error) {
          if (typeof err.response.data.error === 'string') {
            errorMsg = err.response.data.error;
          } else if (err.response.data.error.message) {
            errorMsg = err.response.data.error.message;
          }
        } else if (err.message) {
          errorMsg = err.message;
        }
        setMessage(errorMsg);
        showError(errorMsg);
      }
    };

    verifyEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only depend on token - useRef prevents duplicate calls

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 px-4 transition-colors">
      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
      <div className="max-w-md w-full bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-8 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
        <h2 className="text-3xl font-bold text-center text-shayek-light-900 dark:text-shayek-dark-900 mb-8">
          Email Verification
        </h2>

        {status === 'verifying' && (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-shayek-primary mb-4"></div>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700">
              Verifying your email address...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-green-800 dark:text-green-300 mb-2">
              Email Verified!
            </h3>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700 mb-4">
              {message}
            </p>
            <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
              Redirecting you to your dashboard...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-red-800 dark:text-red-300 mb-2">
              Verification Failed
            </h3>
            <p className="text-shayek-light-700 dark:text-shayek-dark-700 mb-4">
              {message}
            </p>
            <div className="space-y-2">
              <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
                The verification link may have expired or is invalid.
              </p>
              <ResendVerificationForm />
              <div className="flex flex-col gap-2 mt-4">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-shayek-light-200 dark:bg-shayek-dark-200 text-shayek-light-900 dark:text-shayek-dark-900 py-2 px-4 rounded-lg hover:bg-shayek-light-300 dark:hover:bg-shayek-dark-300 focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;

